import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  Edge as ReactFlowEdge,
  Handle,
  MarkerType,
  Node as ReactFlowNode,
  NodeProps,
  NodeToolbar,
  NodeTypes as ReactFlowNodeTypes,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useOnViewportChange,
  useReactFlow,
  useUpdateNodeInternals,
  Viewport,
} from 'reactflow';
import 'reactflow/dist/style.css';
import styled from 'styled-components';
import { stringUtils } from '../../../infrastructure/utils/string-utils';
import { colorsPalette } from '../../styles/colors-palette';
import { flowChartUtils } from '../../../infrastructure/utils/flow-chart-utils';
import { Node } from '@reactflow/core/dist/esm/types/nodes';

interface FlowChartProps<NodeID extends string, NodeTypeID extends string> {
  nodes: FlowNode<NodeID, NodeTypeID>[];
  edges: FlowEdge<NodeID>[];
  nodeTypes?: FlowNodeType<NodeTypeID>[];
  showZoom?: boolean;
  width: CSSSize;
  height: CSSSize;
  autoLayout?: boolean;

  // containerWidth
  // containerHeight
}

interface CustomNodeProps {
  renderContent: () => ReactElement;
  renderWrapper?: (children: ReactNode, isSelected?: boolean) => ReactElement; // will it work?
  renderToolbar?: () => ReactElement;
  isHighlighted?: boolean;
}

interface FlowNode<NodeID extends string, NodeTypeID extends string> {
  id: NodeID;
  content: ReactNode;
  onClick?: () => void;
  position?: NodePosition;
  type?: NodeTypeID;
  renderToolbar?: () => ReactElement;
}

interface FlowEdge<NodeID extends string> {
  id?: string; // TODO use generic type here?
  source: NodeID;
  target: NodeID;
  label?: ReactNode;
  sourcePort?: FlowNodeSourcePort;
  targetPort?: FlowNodeTargetPort;
}

interface NodePosition {
  x: number;
  y: number;
}

interface FlowNodeType<NodeTypeID extends string> {
  id: NodeTypeID;
  renderWrapper: (children: ReactNode, isSelected?: boolean) => ReactElement;
  // availablePorts: AvailablePorts; // TODO add later
}

enum FlowNodeSourcePort {
  TOP = 'source-top',
  BOTTOM = 'source-bottom',
  LEFT = 'source-left',
  RIGHT = 'source-right',
}

enum FlowNodeTargetPort {
  TOP = 'target-top',
  BOTTOM = 'target-bottom',
  LEFT = 'target-left',
  RIGHT = 'target-right',
}

enum AvailablePorts {
  TOP,
  TOP_BOTTOM,
  BOTTOM,
}

const reactFlowNodeTypes = {
  custom: CustomNode,
  // Add any of your custom nodes here!
} as ReactFlowNodeTypes;

const TIMEOUT_TO_CLOSE_TOOLBAR = 500;

function useLayout(initialNodes, initialEdges, autoLayout) {
  const flow = useReactFlow();
  const [flowNodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [flowEdges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [layoutedNodes, setLayoutedNodes] = useState<ReactFlowNode[]>([]);

  const options = {
    includeHiddenNodes: false,
  };
  const nodesInitialized = useNodesInitialized(options);
  // const nodesInitialized = useNodesInitialized({ includeHiddenNodes: false });
  // const updateNodeInternals = useUpdateNodeInternals();

  // const onLayout = useCallback(() => {
  //   const layouted = flowChartUtils.getLayoutedElements(flowNodes, flowEdges, { direction: 'TB' });
  //
  //   setNodes([...layouted.nodes]);
  //   setEdges([...layouted.edges]);
  //   console.log('layouted');
  //
  //   window.requestAnimationFrame(() => {
  //     flow.fitView();
  //   });
  // }, [flowNodes, flowEdges, flow]);

  // useEffect(() => {
  //   if (nodesInitialized) {
  //     const layouted = flowChartUtils.getLayoutedElements(flowNodes, flowEdges, {
  //       direction: 'TB',
  //     });
  //
  //     setNodes([...layouted.nodes]);
  //     setEdges([...layouted.edges]);
  //     window.requestAnimationFrame(() => {
  //       flow.fitView();
  //     });
  //   }
  // }, [flowNodes, flowEdges, flow]); // [flowNodes.length > 0 && flowNodes[0].width]

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges]);

  useEffect(() => {
    if (autoLayout === true) {
      const layouted = flowChartUtils.getLayoutedElements(flowNodes, flowEdges, {
        direction: 'TB',
      });

      setLayoutedNodes(layouted.nodes);
    }
  }, [flowNodes]);

  useEffect(() => {
    if (nodesInitialized) {
      setTimeout(
        () =>
          window.requestAnimationFrame(() => {
            flow.fitView();
          }),
        100,
      );
    }
  }, [nodesInitialized]);

  // useEffect(() => {
  //   window.requestAnimationFrame(() => {
  //     flow.fitView();
  //   });
  // }, [flowNodes[0]]);

  const resultNodes = useMemo(() => {
    const result: ReactFlowNode[] = [];
    if (layoutedNodes.length > 0) {
      flowNodes.forEach(node => {
        const found = layoutedNodes.find(layouted => layouted.id === node.id);

        if (found) {
          result.push({ ...node, position: found.position });
        }
      });
    }

    return result;
  }, [flowNodes, layoutedNodes]);

  return {
    flowNodes: autoLayout ? resultNodes : flowNodes,
    flowEdges: flowEdges,
    onNodesChange,
    onEdgesChange,
    setEdges,
    setNodes,
  };
}

function FlowChart<NodeID extends string, NodeTypeID extends string>({
  nodes,
  edges,
  nodeTypes,
  height,
  width,
  autoLayout,
}: FlowChartProps<NodeID, NodeTypeID>): ReactElement {
  const initialNodes: ReactFlowNode[] = useMemo(
    () =>
      nodes.map(node => {
        let renderWrapper;

        if (node.type && nodeTypes) {
          renderWrapper = nodeTypes.find(type => node.type === type.id)?.renderWrapper;
        }

        return {
          id: node.id,
          type: 'custom',
          position: node.position
            ? {
                x: node.position.x,
                y: node.position.y,
              }
            : { x: 0, y: 0 },
          data: {
            label: node.id,
            renderContent: node.content,
            renderWrapper: renderWrapper,
            renderToolbar: node.renderToolbar,
          },
        };
      }),
    [nodes, edges],
  );

  const initialEdges: ReactFlowEdge[] = useMemo(
    () =>
      edges.map(edge => {
        return {
          id: edge.id ?? stringUtils.generateRandomUniqString(10), // TODO maybe need id here or not? can it produce extra edges rendering?
          source: edge.source,
          target: edge.target,
          label: edge.label,
          labelStyle: { fontSize: '16px' }, // TODO add label as ReactNode
          targetHandle: edge.targetPort,
          sourceHandle: edge.sourcePort,
          animated: false,
          interactionWidth: 30,
          // type: 'smoothstep',
          markerEnd: {
            type: MarkerType.Arrow,
            width: 30,
            height: 30,
          },
          lala: 'lala',
        };
      }),
    [nodes, edges],
  );

  const { flowNodes, flowEdges, onNodesChange, onEdgesChange, setEdges, setNodes } = useLayout(
    initialNodes,
    initialEdges,
    autoLayout,
  );

  // TODO refactor with react flow v12
  const toggleHighlightNodeConnections = (node: Node, highlight: boolean) => {
    if (highlight) {
      setEdges(prevEdges =>
        prevEdges.map(prevEdge => {
          if (prevEdge.source === node.id || prevEdge.target === node.id) {
            return { ...prevEdge, animated: true, selected: highlight };
          } else return prevEdge;
        }),
      );
    } else {
      setEdges(prevEdges =>
        prevEdges.map(prevEdge => {
          return { ...prevEdge, animated: false, selected: false };
        }),
      );
    }

    edges.forEach(edge => {
      if (edge.source === node.id || edge.target === node.id) {
        toggleHighlightNodes(edge as Edge, highlight);
      }
    });
  };

  const toggleHighlightEdge = (edge: Edge, highlight: boolean) => {
    if (highlight) {
      setEdges(prevEdges =>
        prevEdges.map(prevEdge => {
          if (prevEdge.id === edge.id) {
            return { ...prevEdge, animated: true, selected: highlight };
          } else return prevEdge;
        }),
      );
    } else {
      setEdges(prevEdges =>
        prevEdges.map(prevEdge => {
          return { ...prevEdge, animated: false, selected: false };
        }),
      );
    }
  };

  const toggleHighlightNodes = (edge: Edge, highlight: boolean) => {
    const target = edge.target;
    const source = edge.source;

    if (highlight) {
      setNodes(prevNodes => {
        return prevNodes.map(prevNode => {
          if (prevNode.id === target || prevNode.id === source) {
            return {
              ...prevNode,
              data: {
                ...prevNode.data,
                isHighlighted: true,
              },
            };
          } else {
            return prevNode;
          }
        });
      });
    } else {
      setNodes(prevNodes => {
        return prevNodes.map(prevNode => {
          return {
            ...prevNode,
            data: {
              ...prevNode.data,
              isHighlighted: false,
            },
          };
        });
      });
    }
  };

  return (
    <>
      {/* TODO make with styled*/}
      <FlowAreaWrapper style={{ width: width, height: height }}>
        {/*<Button onClick={() => onLayout()}>onLayout</Button>*/}
        <ReactFlowStyled
          nodeTypes={reactFlowNodeTypes}
          nodes={flowNodes}
          edges={flowEdges}
          elevateEdgesOnSelect={true}
          onNodesChange={changes => {
            onNodesChange(changes);
          }}
          nodesDraggable={false}
          onEdgesChange={onEdgesChange}
          fitView={true}
          onNodeMouseEnter={(event, node) => {
            toggleHighlightNodeConnections(node, true);
          }}
          onNodeMouseLeave={(event, node) => {
            toggleHighlightNodeConnections(node, false);
          }}
          onEdgeMouseEnter={(event, edge) => {
            toggleHighlightEdge(edge, true);
            toggleHighlightNodes(edge, true);
          }}
          onEdgeMouseLeave={(event, edge) => {
            toggleHighlightEdge(edge, false);
            toggleHighlightNodes(edge, false);
          }}
        >
          <Controls showInteractive={false} position={'top-left'} />
          <Background
            color={colorsPalette.gray100}
            variant={BackgroundVariant.Dots}
            gap={10}
            size={1}
          />
        </ReactFlowStyled>
      </FlowAreaWrapper>
    </>
  );
}

function CustomNode({ xPos, yPos, data }: NodeProps<CustomNodeProps>) {
  const x = `${Math.round(xPos)}px`;
  const y = `${Math.round(yPos)}px`;
  const [toolbarIsVisible, setToolbarIsVisible] = useState<boolean | undefined>(false);

  return (
    <StyledNode
      onMouseEnter={() => setToolbarIsVisible(true)}
      onMouseLeave={() => setToolbarIsVisible(undefined)}
      // isHighlighted={data.isHighlighted}
    >
      <StyledNodeToolBar isVisible={toolbarIsVisible} position={Position.Top} align={'end'}>
        {data.renderToolbar && data.renderToolbar()}
      </StyledNodeToolBar>
      {data.renderWrapper ? (
        <>
          <Handle type="target" position={Position.Top} id={FlowNodeTargetPort.TOP} />
          {data.renderWrapper(<>{data.renderContent}</>, data.isHighlighted)}
          <Handle type="source" position={Position.Bottom} id={FlowNodeSourcePort.BOTTOM} />
        </>
      ) : (
        <div className="react-flow__node-default">
          <Handle type="target" position={Position.Top} id={FlowNodeTargetPort.TOP} />
          <>{data.renderContent}</>
          {/*<div>*/}
          {/*  {x} {y}*/}
          {/*</div>*/}
          <Handle type="source" position={Position.Bottom} id={FlowNodeSourcePort.BOTTOM} />
        </div>
      )}
    </StyledNode>
  );
}

function FlowChartWithContext<NodeID extends string, NodeTypeID extends string>(
  props: FlowChartProps<NodeID, NodeTypeID>,
): ReactElement {
  return (
    <ReactFlowProvider>
      <FlowChart {...props} />
    </ReactFlowProvider>
  );
}

const FlowAreaWrapper = styled.div`
  border: 1px solid ${colorsPalette.lightGray300};
`;

const StyledNode = styled.div<{ isHighlighted?: boolean }>`
  .react-flow__handle {
    visibility: hidden;
  }
  transition: 300ms ease-in-out;
  transform: ${props => (props.isHighlighted === true ? 'scale(1.15) skew(-5deg)' : 'scale(1)')};
`;

const StyledNodeToolBar = styled(NodeToolbar)`
  top: 20px;
`;

const ReactFlowStyled = styled(ReactFlow)`
  .react-flow__attribution {
    display: none;
  }
` as typeof ReactFlow;

export {
  FlowChartWithContext as FlowChart,
  type FlowNode,
  type FlowEdge,
  type FlowNodeType,
  AvailablePorts,
  FlowNodeSourcePort,
  FlowNodeTargetPort,
};
